import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Printer, Download, ShoppingCart, Mail, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PurchaseHistoryModal = ({ open, onClose, customer }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [sendingInvoice, setSendingInvoice] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  useEffect(() => {
    if (open && customer) {
      fetchPurchaseHistory();
    }
  }, [open, customer, startDate, endDate]);

  const fetchPurchaseHistory = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/customers/${customer.id}/purchase-history?start_date=${startDate}&end_date=${endDate}`
      );
      setItems(response.data.items || []);
    } catch (error) {
      console.error("Error fetching purchase history:", error);
      toast.error("Failed to load purchase history");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (items.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = ["InvNo", "Date", "PLU", "Description", "Qty", "Retail", "Total", "User"];
    const csvContent = [
      headers.join(","),
      ...items.map(item => [
        item.inv_no,
        item.date,
        item.plu,
        `"${item.description}"`,
        item.qty,
        item.retail.toFixed(2),
        item.total.toFixed(2),
        item.user
      ].join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `purchase_history_${customer.name}_${startDate}_${endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export completed");
  };

  const handlePrint = () => {
    const printContent = `
      <html>
        <head>
          <title>Purchase History - ${customer.name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 18px; margin-bottom: 5px; }
            h2 { font-size: 14px; color: #666; margin-bottom: 20px; }
            table { width: 100%; border-collapse: collapse; font-size: 11px; }
            th, td { border: 1px solid #ddd; padding: 6px; text-align: left; }
            th { background: #f5f5f5; font-weight: bold; }
            .number { text-align: right; }
            .total-row { font-weight: bold; background: #f9f9f9; }
          </style>
        </head>
        <body>
          <h1>Purchase History: ${customer.name}</h1>
          <h2>Account: ${customer.account_number} | Period: ${startDate} to ${endDate}</h2>
          <table>
            <thead>
              <tr>
                <th>InvNo</th>
                <th>Date</th>
                <th>PLU</th>
                <th>Description</th>
                <th class="number">Qty</th>
                <th class="number">Retail</th>
                <th class="number">Total</th>
                <th>User</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(item => `
                <tr>
                  <td>${item.inv_no}</td>
                  <td>${item.date}</td>
                  <td>${item.plu}</td>
                  <td>${item.description}</td>
                  <td class="number">${item.qty}</td>
                  <td class="number">R ${item.retail.toFixed(2)}</td>
                  <td class="number">R ${item.total.toFixed(2)}</td>
                  <td>${item.user}</td>
                </tr>
              `).join('')}
              <tr class="total-row">
                <td colspan="4">TOTAL</td>
                <td class="number">${items.reduce((sum, i) => sum + i.qty, 0)}</td>
                <td></td>
                <td class="number">R ${items.reduce((sum, i) => sum + i.total, 0).toFixed(2)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Send individual invoice via email
  const handleEmailInvoice = async (item) => {
    if (!customer.email) {
      toast.error("Customer does not have an email address. Please add one in customer details.");
      return;
    }

    const saleId = item.sale_id || item.id;
    if (!saleId) {
      toast.error("Invoice ID not found. Cannot send email.");
      return;
    }

    setSendingInvoice(saleId);
    try {
      await axios.post(`${API}/email/sale-invoice`, {
        sale_id: saleId,
        recipient_email: customer.email
      });
      toast.success(`Invoice ${item.inv_no} sent to ${customer.email}`);
    } catch (error) {
      console.error("Error sending invoice:", error);
      const errorMessage = error.response?.data?.detail || "Failed to send invoice";
      if (errorMessage.includes("disabled")) {
        toast.error("Emails are disabled in system settings. Enable them in Settings > Email.");
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setSendingInvoice(null);
    }
  };

  if (!customer) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ShoppingCart className="h-5 w-5" />
            Purchase History: {customer.name}
          </DialogTitle>
          <div className="text-sm text-gray-500">
            Account: {customer.account_number}
          </div>
        </DialogHeader>

        {/* Date Filters */}
        <div className="flex items-center gap-4 py-2 border-b">
          <div className="flex items-center gap-2">
            <Label className="text-sm">From:</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-40"
            />
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-sm">To:</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-40"
            />
          </div>
          <div className="flex-1" />
          <Button variant="outline" size="sm" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-1" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-1" />
            Print
          </Button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-gray-500">
              <ShoppingCart className="h-12 w-12 mb-2 opacity-50" />
              <p>No purchases found for this period</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>InvNo</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>PLU</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">Retail</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead className="text-center">Email</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-medium">{item.inv_no}</TableCell>
                    <TableCell>{item.date}</TableCell>
                    <TableCell className="text-gray-500">{item.plu}</TableCell>
                    <TableCell>{item.description}</TableCell>
                    <TableCell className="text-right">{item.qty}</TableCell>
                    <TableCell className="text-right">R {item.retail.toFixed(2)}</TableCell>
                    <TableCell className="text-right font-medium">R {item.total.toFixed(2)}</TableCell>
                    <TableCell className="text-gray-500">{item.user}</TableCell>
                    <TableCell className="text-center">
                      {item.sale_id && customer.email ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEmailInvoice(item)}
                          disabled={sendingInvoice === item.sale_id}
                          className="h-7 px-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          title={`Email invoice ${item.inv_no} to ${customer.email}`}
                          data-testid={`email-invoice-${item.inv_no}`}
                        >
                          {sendingInvoice === item.sale_id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Send className="h-3 w-3" />
                          )}
                        </Button>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {/* Totals Row */}
                <TableRow className="bg-gray-100 font-bold">
                  <TableCell colSpan={4}>TOTAL ({items.length} items)</TableCell>
                  <TableCell className="text-right">{items.reduce((sum, i) => sum + i.qty, 0)}</TableCell>
                  <TableCell></TableCell>
                  <TableCell className="text-right">R {items.reduce((sum, i) => sum + i.total, 0).toFixed(2)}</TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseHistoryModal;
